import React, { useMemo } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { IntegratedErps } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { Switch } from 'antd';
import moment from 'moment';
import { theme } from 'theme';

import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { formatPercent, formatUSDAbbreviated } from 'utils/formatting';
import { useHashState } from 'utils/routes';
import { isTenantErpTypeNetSuite } from 'utils/tenants';
import { getTenantSetting } from 'utils/tenantSettings';

import { InventoryDashboardHashState, InventoryDashboardHashStateFilters } from 'types/hash-state';

import { MetricsCard } from './ExecutiveMetricsPage/MetricsCard';
import { InventoryBarChart } from './InventoryBarChart';
import { InventoryDashboardFilters } from './shared/InventoryDashboardFilters';
import { useInventoryReportFilters } from './useInventoryReportFilters';

export const ExecutiveMetricsPage = () => {
  const [hashState, updateHashState] = useHashState<InventoryDashboardHashState>();
  const { activeTenant } = useGlobalApp();
  const fillRateGracePeriod = getTenantSetting(TenantSettingKey.CalculationFillRateOnTimeDays);

  const [showInventoryTransfers, setShowInventoryTransfers] = React.useState(false);

  const reportFilters = useInventoryReportFilters();

  const {
    data: weeklyData,
    isLoading: weeklyIsLoading,
    isReloading: weeklyIsReloading,
  } = useCoreApi(schemas.inventory.getInventoryMetricsReport, {
    queryParams: {
      filters: reportFilters,
    },
  });

  const {
    data: transfersData,
    isLoading: transfersIsLoading,
    isReloading: transfersIsReloading,
  } = useCoreApi(schemas.inventory.getInventoryTransfersReport, {
    queryParams: {
      filters: reportFilters,
    },
  });

  // Merge weekly data with transfers data
  const inflowOutflowDataItems = useMemo(
    () =>
      weeklyData?.items.map((i) => ({
        ...i,
        ...(transfersData?.items.find((t) => t.date === i.date) || {
          inventoryTransfersIn: 0,
          inventoryTransfersOut: 0,
          inventoryTransfersBetween: 0,
        }),
      })),
    [weeklyData?.items, transfersData?.items],
  );

  const onHashStateFilterChange = (filters: InventoryDashboardHashStateFilters) => {
    updateHashState({ ...hashState, filters });
  };

  return (
    <>
      <InventoryDashboardFilters
        label="Filter"
        filterState={hashState.filters}
        onFiltersChange={onHashStateFilterChange}
        showItemFilter
        showLocationFilter
        showStockableFilter
        showRulesFilter
        showBuyerFilter={getTenantSetting(TenantSettingKey.UiShowBuyerFilter)}
        showPurchaseClassFilter={!isTenantErpTypeNetSuite(activeTenant.erpType)}
        showProductGroupFilter={!isTenantErpTypeNetSuite(activeTenant.erpType)}
      />
      <DividerLine />
      <FlexSpace
        direction="column"
        className={css`
          width: 100%;
          height: 100%;
        `}
        gap={16}
      >
        {/* ************** */}
        {/* Inflow/Outflow */}
        {/* ************** */}
        <MetricsCard
          title="Weekly Inflow/Outflow"
          description="Sum of incoming purchase orders, outgoing sales orders, and transfers between locations."
          loading={
            weeklyIsLoading ||
            weeklyIsReloading ||
            (showInventoryTransfers ? transfersIsLoading || transfersIsReloading : false)
          }
          headerActions={
            <FlexSpace>
              <b>Show transfers</b>
              <Switch checked={showInventoryTransfers} onClick={setShowInventoryTransfers} />
            </FlexSpace>
          }
        >
          <InventoryBarChart
            data={inflowOutflowDataItems?.map((item) => ({
              date: item.date,
              inventoryInflow: item.inventoryInflow,
              inventoryOutflow: item.inventoryOutflow * -1,
              inventoryTransfersIn: showInventoryTransfers ? item.inventoryTransfersIn : 0,
              inventoryTransfersOut: showInventoryTransfers ? item.inventoryTransfersOut * -1 : 0,
              inventoryTransfersBetween: showInventoryTransfers ? item.inventoryTransfersBetween / 2 : 0,
              inventoryTransfersBetweenNegative: showInventoryTransfers ? (item.inventoryTransfersBetween / 2) * -1 : 0,
            }))}
            valueFormatter={(value) => formatUSDAbbreviated(value)}
            xAxisFormatter={(value) => moment(value).format('M/D')}
            tooltipTitle={(payload) => `Week of ${moment(payload.date).format('M/D')}`}
            bars={[
              // Positive bars
              ...(showInventoryTransfers
                ? [
                    {
                      key: 'inventoryTransfersBetween',
                      color: theme.colors.chart.sunriseYellow,
                      secondaryColor: theme.colors.chartLight.sunriseYellow,
                      title: 'Inventory Transfers Between',
                      // Double the value to account for both sides of the "in between" transfer
                      valueFormatter: (value: number) => formatUSDAbbreviated(value * 2),
                    },
                    {
                      key: 'inventoryTransfersIn',
                      color: theme.colors.chart.oceanTeal,
                      secondaryColor: theme.colors.chartLight.oceanTeal,
                      title: 'Inventory Transfers In',
                    },
                  ]
                : []),
              {
                key: 'inventoryInflow',
                color: theme.colors.chart.malibuOrange,
                secondaryColor: theme.colors.chartLight.malibuOrange,
                title: 'Inventory Inflow',
              },

              // Negative bars
              ...(showInventoryTransfers
                ? [
                    {
                      key: 'inventoryTransfersBetweenNegative',
                      color: theme.colors.chart.sunriseYellow,
                      secondaryColor: theme.colors.chartLight.sunriseYellow,
                      title: 'Inventory Transfers Between',
                      showInTooltip: false,
                    },
                    {
                      key: 'inventoryTransfersOut',
                      color: theme.colors.chart.aloeGreen,
                      secondaryColor: theme.colors.chartLight.aloeGreen,
                      title: 'Inventory Transfers Out',
                    },
                  ]
                : []),
              {
                key: 'inventoryOutflow',
                color: theme.colors.chart.lagoonBlue,
                secondaryColor: theme.colors.chartLight.lagoonBlue,
                title: 'Inventory Outflow',
              },
            ]}
            netSum
            inProgressDate={moment().startOf('week').format('YYYY-MM-DD')}
          />
        </MetricsCard>

        {/* ************** */}
        {/*   Fill Rate    */}
        {/* ************** */}
        <MetricsCard
          title="Weekly Fill Rate"
          description="Percentage of items on orders that shipped complete and on time each week, excluding cancellations. Completeness is per item; on time means shipped within the configured grace period of the order’s requested date."
          loading={weeklyIsLoading || weeklyIsReloading}
        >
          <InventoryBarChart
            data={
              weeklyData?.items ? weeklyData.items.map((item) => ({ date: item.date, fillRate: item.fillRate })) : []
            }
            valueFormatter={(value) => formatPercent(value / 100)}
            xAxisFormatter={(value) => moment(value).format('M/D')}
            tooltipTitle={(payload) => `Week of ${moment(payload.date).format('M/D')}`}
            bars={[
              {
                key: 'fillRate',
                color: theme.colors.chart.oceanTeal,
                secondaryColor: theme.colors.chartLight.oceanTeal,
                title: 'Fill Rate',
              },
            ]}
            percentageDomain
            movingAverage
            inProgressDate={moment().subtract(fillRateGracePeriod, 'days').format('YYYY-MM-DD')}
          />
        </MetricsCard>

        {/* ************** */}
        {/* New Backorders */}
        {/* ************** */}
        {activeTenant.erpType !== IntegratedErps.NETSUITE && (
          <MetricsCard
            title="Weekly New Backorders"
            description="The sum of all new backorders created each week based on inventory transactions and their costs."
            loading={weeklyIsLoading || weeklyIsReloading}
          >
            <InventoryBarChart
              data={
                weeklyData?.items
                  ? weeklyData.items.map((item) => ({ date: item.date, newBackorders: item.newBackorders }))
                  : []
              }
              valueFormatter={(value) => formatUSDAbbreviated(value)}
              xAxisFormatter={(value) => moment(value).format('M/D')}
              tooltipTitle={(payload) => `Week of ${moment(payload.date).format('M/D')}`}
              bars={[
                {
                  key: 'newBackorders',
                  color: theme.colors.chart.malibuOrange,
                  secondaryColor: theme.colors.chartLight.malibuOrange,
                  title: 'New Backorders',
                },
              ]}
              movingAverage
              inProgressDate={moment().startOf('week').format('YYYY-MM-DD')}
            />
          </MetricsCard>
        )}
      </FlexSpace>
    </>
  );
};
